class CondenseContent extends HTMLElement {
  constructor() {
    super()

    this.toggleButton = null
    this.truncateContent = null
    this.truncateContentMobile = null
    this.completeContent = null
    this.showMoreText = null
    this.showLessText = null

    this._toggleHandler = null
    this._initialised = false
  }

  connectedCallback() {
    if (this._initialised) return

    this.toggleButton = this.querySelector('.content-toggle')
    this.truncateContent = this.querySelector('.condensed-content')
    this.truncateContentMobile = this.querySelector('.condensed-content-mobile')
    this.completeContent = this.querySelector('.complete-content')
    this.showMoreText = this.dataset.showMoreText
    this.showLessText = this.dataset.showLessText

    if (!this.toggleButton || !this.truncateContent) {
      console.warn('[CondenseContent] Required elements missing', {
        element: this,
        toggleButton: this.toggleButton,
        truncateContent: this.truncateContent,
      })
      return
    }

    this._toggleHandler = () => {
      const expanding = this.classList.contains('collapsed')

      if (expanding) {
        this.truncateContent.classList.add('hidden')

        if (this.truncateContentMobile) {
          this.truncateContentMobile.classList.add('hidden')
        }

        if (this.completeContent) {
          this.completeContent.classList.remove('hidden')
        }

        this.toggleButton.textContent = this.showLessText
      } else {
        this.truncateContent.classList.remove('hidden')

        if (this.truncateContentMobile) {
          this.truncateContentMobile.classList.remove('hidden')
        }

        if (this.completeContent) {
          this.completeContent.classList.add('hidden')
        }

        this.toggleButton.textContent = this.showMoreText
      }

      this.classList.toggle('collapsed')
      this.toggleButton.setAttribute('aria-expanded', expanding ? 'true' : 'false')
    }

    if (this.toggleButton.tagName !== 'BUTTON') {
      this._keydownHandler = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          this._toggleHandler()
        }
      }
      this.toggleButton.setAttribute('role', 'button')
      this.toggleButton.setAttribute('tabindex', '0')
      this.toggleButton.addEventListener('keydown', this._keydownHandler)
    }
    this.toggleButton.setAttribute('aria-expanded', this.classList.contains('collapsed') ? 'false' : 'true')

    this.toggleButton.addEventListener('click', this._toggleHandler)

    this._initialised = true
  }

  disconnectedCallback() {
    if (this.toggleButton && this._toggleHandler) {
      this.toggleButton.removeEventListener('click', this._toggleHandler)
    }
    if (this.toggleButton && this._keydownHandler) {
      this.toggleButton.removeEventListener('keydown', this._keydownHandler)
    }

    this._initialised = false
  }
}

if (!customElements.get('condensed-content')) {
  customElements.define('condensed-content', CondenseContent)
}


function getSwatchesModalFocusable(container) {
  return Array.prototype.slice
    .call(
      container.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    )
    .filter((el) => el.offsetWidth || el.offsetHeight || el.getClientRects().length)
}

/*
 * Nosto-rendered swatch modals have no Liquid-authored id on their <h3>, so
 * the accessible name (aria-labelledby) has nothing to point at. Generates a
 * unique id at runtime for exactly that case; theme-rendered grids already
 * carry a Liquid id (SwatchesModalTitle--{{ item_unique }}) and this counter
 * never collides with it.
 */
let swatchesModalTitleCounter = 0
function nextSwatchesModalTitleId() {
  swatchesModalTitleCounter += 1
  return 'SwatchesModalTitle--js-' + swatchesModalTitleCounter
}

class SwatchesModal extends HTMLElement {
  constructor() {
    super()

    this.modal = null
    this.closeButton = null
    this.openButton = null
    this.productItem = null

    /*
     * Global swatches-modal backdrop, shared by every <swatches-modal>
     * instance on the page (replaces the old per-card .overlay, which
     * Nosto's AU cards never emit - see PROJECT_PLAN.md TASK-015). It must
     * remain a single, permanent child of document.body for the life of the
     * page: no instance may remove or reparent it, only toggle its
     * aria-hidden state while that instance's own modal is open.
     */
    this.overlay = document.getElementById('swatches-modal-global-overlay')
    if (!this.overlay) {
      this.overlay = document.createElement('div')
      this.overlay.id = 'swatches-modal-global-overlay'
      this.overlay.className = 'swatches-modal-backdrop'
      this.overlay.setAttribute('aria-hidden', 'true')
      document.body.appendChild(this.overlay)
    }

    this._modalPlaceholder = null
    this._initialised = false
    this._initFrame = null
    this._closeTimeout = null
    this._bodyListenerAttached = false
    this._keydownHandler = null
    this.lastFocusedElement = null

    this._openHandler = () => {
      this.openModal()
    }

    this._openKeydownHandler = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        this.openModal()
      }
    }

    this._closeHandler = () => {
      this.closeModal()
    }

    this._overlayClickHandler = (event) => {
      /*
       * Preserve the behaviour of the existing implementation:
       * clicking the modal backdrop closes the modal.
       */
      if (event.target === this.modal) {
        this.closeModal()
      }
    }
  }

  connectedCallback() {
    /*
     * Do not initialise repeatedly if this custom element gets
     * disconnected/reconnected by dynamic PLP rendering.
     */
    if (this._initialised) return

    /*
     * Wait until the next frame before querying child elements.
     *
     * This is safer than doing these queries in the constructor because
     * a custom element may be upgraded before all of its child markup
     * has been parsed/inserted.
     */
    this._initFrame = requestAnimationFrame(() => {
      this._initFrame = null

      if (!this.isConnected) return

      this.init()
    })
  }

  init() {
    if (this._initialised) return

    this.modal = this.querySelector(
      '.product-item__swatches__modal'
    )

    this.openButton = this.querySelector(
      '.product-item__swatches__count.product-item__swatches__count--swatches-modal-new'
    )

    this.productItem = this.closest('.product-item')

    this.closeButton = this.modal?.querySelector(
      '.product-item__swatches__modal__inner__header__close__button'
    )

    /*
     * The old per-card .overlay dimming layer is superseded by the shared
     * global backdrop (set up in the constructor). Remove it if present -
     * Nosto's AU cards never emit one at all, and it must not be required
     * for a card to initialise (see the check below).
     */
    const localOverlay = this.querySelector('.overlay')
    if (localOverlay) localOverlay.remove()

    /*
     * IMPORTANT DEBUG:
     *
     * Previously the code immediately did:
     *
     * this.modal.querySelector(...)
     *
     * even when this.modal was null.
     *
     * This warning identifies exactly which product/card contains
     * incomplete SwatchesModal markup without throwing an exception.
     */
    if (
      !this.modal ||
      !this.closeButton ||
      !this.openButton ||
      !this.productItem
    ) {
      console.warn('[SwatchesModal] Cannot initialise - required element missing', {
        swatchesModal: this,
        modal: this.modal,
        closeButton: this.closeButton,
        openButton: this.openButton,
        productItem: this.productItem,
      })

      return
    }

    this._modalPlaceholder = document.createComment(
      'swatches-modal-portal'
    )

    /*
     * Same position as the previous implementation.
     */
    this.modal.after(this._modalPlaceholder)

    /*
     * Dialog semantics, applied here in JS (not just Liquid) because Nosto
     * re-renders these cards with its own markup, dropping any Liquid-authored
     * attributes entirely - see PROJECT_PLAN.md TASK-015.
     *
     * The modal is hidden by sliding off-screen (bottom/right: -100%), never
     * by display:none, so its ~150 sibling links stay in the tab order even
     * though they're invisible. Mark it inert by default and only lift that
     * in openModal().
     */
    this.modal.setAttribute('role', 'dialog')
    this.modal.setAttribute('aria-modal', 'true')
    this.modal.setAttribute('tabindex', '-1')
    this.modal.setAttribute('aria-hidden', 'true')
    this.modal.setAttribute('inert', '')
    this.openButton.setAttribute('aria-expanded', 'false')

    const heading = this.modal.querySelector('h3')
    if (heading) {
      if (!heading.id) heading.id = nextSwatchesModalTitleId()
      this.modal.setAttribute('aria-labelledby', heading.id)
    } else {
      const productTitle = this.openButton.getAttribute('aria-label')
      if (productTitle) this.modal.setAttribute('aria-label', productTitle)
    }

    /*
     * The trigger is a <span> with no native keyboard support - make it
     * behave like a real button.
     */
    if (!this.openButton.hasAttribute('role')) {
      this.openButton.setAttribute('role', 'button')
    }
    if (!this.openButton.hasAttribute('tabindex')) {
      this.openButton.setAttribute('tabindex', '0')
    }

    this.openButton.addEventListener(
      'click',
      this._openHandler
    )

    this.openButton.addEventListener(
      'keydown',
      this._openKeydownHandler
    )

    this.closeButton.addEventListener(
      'click',
      this._closeHandler
    )

    this._initialised = true
  }

  disconnectedCallback() {
    /*
     * Cancel initialisation if the product card was removed before
     * requestAnimationFrame ran.
     */
    if (this._initFrame !== null) {
      cancelAnimationFrame(this._initFrame)
      this._initFrame = null
    }

    /*
     * Cancel a delayed close callback so an old product card cannot
     * manipulate DOM after Nosto/another renderer has removed it.
     */
    if (this._closeTimeout !== null) {
      clearTimeout(this._closeTimeout)
      this._closeTimeout = null
    }

    if (this.openButton) {
      this.openButton.removeEventListener(
        'click',
        this._openHandler
      )
      this.openButton.removeEventListener(
        'keydown',
        this._openKeydownHandler
      )
    }

    if (this._keydownHandler) {
      document.removeEventListener('keydown', this._keydownHandler)
      this._keydownHandler = null
    }

    if (this.closeButton) {
      this.closeButton.removeEventListener(
        'click',
        this._closeHandler
      )
    }

    this.removeBodyClickListener()

    /*
     * If this component was the active open modal, hide the shared backdrop
     * - otherwise leave it alone. It belongs to the document, not this
     * instance: never remove or reparent it, and never hide it on behalf of
     * a modal that wasn't the one showing it (a stale disconnect must not be
     * able to hide a second modal's backdrop after it has opened).
     */
    if (this.getAttribute('aria-open') === 'true' && this.overlay) {
      this.overlay.setAttribute('aria-hidden', 'true')
    }

    /*
     * A product card may be removed/re-rendered while its modal has
     * been portalled to <body>.
     *
     * Without cleanup, the old modal could remain orphaned under
     * document.body. The shared backdrop is never removed here.
     */
    if (
      this.modal &&
      this.modal.parentNode === document.body
    ) {
      this.modal.remove()
    }

    if (this._modalPlaceholder) {
      this._modalPlaceholder.remove()
      this._modalPlaceholder = null
    }

    /*
     * Do not leave the global HTML state stuck open when a card
     * disappears while its modal is active.
     */
    if (this.getAttribute('aria-open') === 'true') {
      document.documentElement.removeAttribute(
        'swatches-modal-open'
      )
    }

    this._initialised = false

    this.modal = null
    this.closeButton = null
    this.openButton = null
    this.productItem = null
    this.overlay = null
  }

  addBodyClickListener() {
    if (this._bodyListenerAttached) return

    document.body.addEventListener(
      'click',
      this._overlayClickHandler
    )

    this._bodyListenerAttached = true
  }

  removeBodyClickListener() {
    if (!this._bodyListenerAttached) return

    document.body.removeEventListener(
      'click',
      this._overlayClickHandler
    )

    this._bodyListenerAttached = false
  }

  openModal() {
    if (
      !this._initialised ||
      !this.modal ||
      !this.overlay ||
      !this.productItem
    ) {
      console.warn(
        '[SwatchesModal] openModal() called before successful initialisation',
        this
      )

      return
    }

    /*
     * Cancel any pending delayed close before reopening.
     */
    if (this._closeTimeout !== null) {
      clearTimeout(this._closeTimeout)
      this._closeTimeout = null
    }

    this.closeOtherModals()
    this.lastFocusedElement = document.activeElement

    document.body.appendChild(this.modal)
    /*
     * The global backdrop is already a permanent child of document.body -
     * show it, never append/move it.
     */

    this.addBodyClickListener()

    /*
     * X3 (initAriaHiddenInertSync) mirrors aria-hidden onto inert. Flip
     * aria-hidden to false BEFORE removing inert, so X3 never gets a chance
     * to re-add inert behind our back. This ordering is load-bearing.
     */
    this.modal.setAttribute('aria-hidden', 'false')
    this.modal.removeAttribute('inert')
    this.modal.setAttribute('aria-open', 'true')
    this.openButton.setAttribute('aria-expanded', 'true')
    this.overlay.setAttribute('aria-hidden', 'false')
    this.productItem.setAttribute('aria-open', 'true')
    this.setAttribute('aria-open', 'true')

    this.scrollY = window.scrollY

    document.documentElement.setAttribute(
      'swatches-modal-open',
      'true'
    )

    this._keydownHandler = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.closeModal()
        return
      }

      if (event.key !== 'Tab') return

      const focusable = getSwatchesModalFocusable(this.modal)
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', this._keydownHandler)

    /*
     * Match US: focus the close button, not the first focusable element.
     * getSwatchesModalFocusable() (via _keydownHandler above) still needs to
     * see it as part of the tab order, just no longer as the initial target.
     */
    requestAnimationFrame(() => {
      this.closeButton.focus()
    })
  }

  closeOtherModals() {
    document.querySelectorAll('swatches-modal').forEach((other) => {
      if (other !== this && other.getAttribute('aria-open') === 'true') {
        other.closeModal()
      }
    })
  }

  closeModal() {
    /*
     * Invalid/uninitialised swatches-modal elements should simply
     * do nothing instead of throwing.
     */
    if (!this.modal) {
      return
    }

    /*
     * Only this component's own open state authorises touching the shared
     * backdrop - a redundant closeModal() call (e.g. defensive cleanup on an
     * already-closed component) must never hide it out from under whichever
     * modal actually has it open.
     */
    const wasOpen = this.getAttribute('aria-open') === 'true'

    /*
     * Move focus out before marking the modal inert - an inert subtree
     * can't contain the active element.
     */
    if (this.modal.contains(document.activeElement) && this.openButton) {
      this.openButton.focus()
    }

    this.setAttribute('aria-open', 'false')
    this.modal.setAttribute('aria-open', 'false')
    this.modal.setAttribute('aria-hidden', 'true')
    this.modal.setAttribute('inert', '')
    this.openButton.setAttribute('aria-expanded', 'false')
    if (wasOpen && this.overlay) {
      this.overlay.setAttribute('aria-hidden', 'true')
    }

    if (this.productItem) {
      this.productItem.setAttribute('aria-open', 'false')
    }

    document.documentElement.removeAttribute(
      'swatches-modal-open'
    )

    if (this._keydownHandler) {
      document.removeEventListener('keydown', this._keydownHandler)
      this._keydownHandler = null
    }

    /*
     * Remove the body listener immediately. There is no reason to
     * retain it during the 300ms closing animation.
     */
    this.removeBodyClickListener()

    if (this._closeTimeout !== null) {
      clearTimeout(this._closeTimeout)
    }

    this._closeTimeout = setTimeout(() => {
      this._closeTimeout = null

      /*
       * If the component/card was removed by dynamic rendering during the
       * animation, do not try to restore it into detached markup. Only this
       * component's own portalled modal is ever removed here - the shared
       * backdrop is never touched by this callback.
       */
      if (
        !this.isConnected ||
        !this._modalPlaceholder ||
        !this._modalPlaceholder.isConnected
      ) {
        if (
          this.modal &&
          this.modal.parentNode === document.body
        ) {
          this.modal.remove()
        }

        return
      }

      if (
        this.modal &&
        this.modal.parentNode === document.body
      ) {
        this._modalPlaceholder.after(this.modal)
      }
    }, 300)
  }
}

if (!customElements.get('swatches-modal')) {
  customElements.define('swatches-modal', SwatchesModal)
}


/*
 * DEBUG HELPER
 *
 * Run this manually in Safari's console at any time:
 *
 *     debugSwatchesModal()
 *
 * It reports:
 * - total swatches-modal components
 * - how many are missing their modal
 * - how many are missing their open button
 * - how many are missing their close button
 * - how many #swatches-modal-global-overlay elements exist under document.body
 *   (must be exactly 1 - the global-backdrop architecture means a healthy
 *   card never has its own local overlay, so this replaces the old
 *   per-card missingOverlay check)
 * - the actual offending elements
 */
window.debugSwatchesModal = function () {
  const components = [
    ...document.querySelectorAll('swatches-modal')
  ]

  const missingModal = components.filter((element) => {
    return !element.querySelector(
      '.product-item__swatches__modal'
    )
  })

  const missingOpenButton = components.filter((element) => {
    return !element.querySelector(
      '.product-item__swatches__count.product-item__swatches__count--swatches-modal-new'
    )
  })

  const missingCloseButton = components.filter((element) => {
    const modal = element.querySelector(
      '.product-item__swatches__modal'
    )

    return (
      modal &&
      !modal.querySelector(
        '.product-item__swatches__modal__inner__header__close__button'
      )
    )
  })

  const globalOverlayCount = document.querySelectorAll(
    '#swatches-modal-global-overlay'
  ).length

  const report = {
    total: components.length,
    missingModal: missingModal.length,
    missingOpenButton: missingOpenButton.length,
    missingCloseButton: missingCloseButton.length,
    globalOverlayCount: globalOverlayCount,
  }

  console.log(
    '[SwatchesModal DEBUG]',
    report
  )

  if (missingModal.length) {
    console.warn(
      '[SwatchesModal DEBUG] Elements missing modal:',
      missingModal
    )
  }

  if (missingOpenButton.length) {
    console.warn(
      '[SwatchesModal DEBUG] Elements missing open button:',
      missingOpenButton
    )
  }

  if (missingCloseButton.length) {
    console.warn(
      '[SwatchesModal DEBUG] Elements missing close button:',
      missingCloseButton
    )
  }

  if (globalOverlayCount !== 1) {
    console.warn(
      '[SwatchesModal DEBUG] Expected exactly 1 #swatches-modal-global-overlay, found:',
      globalOverlayCount
    )
  }

  return report
}


/*
 * Run the diagnostic automatically once the page has settled.
 */
window.addEventListener('load', () => {
  requestAnimationFrame(() => {
    window.debugSwatchesModal()
  })
})


/*
 * Existing pageshow behaviour, now safe for malformed/uninitialised
 * swatches-modal components.
 */
window.addEventListener('pageshow', function () {
  const modalSwatches =
    document.querySelectorAll('swatches-modal')

  modalSwatches.forEach((modal) => {
    modal.closeModal()
  })
})
